import {
  cloneEdges,
  cloneNodes,
  cloneScene,
} from '../documentClone.js';
import type { GraphEdge, GraphNode } from '../../graph/nodeDefs.js';
import type {
  ComponentDef,
  FrameNode,
  InstanceNode,
  LayerNode,
  Scene,
} from '../../scene/sceneState.js';
import { vec2Clone } from '../../scene/layoutMath.js';
import type {
  GeometrySlot,
  LayerDef,
  MaterialSlot,
} from '../../scene/layerState.js';

export function cloneComponentDef(component: ComponentDef): ComponentDef {
  return {
    id: component.id,
    name: component.name,
    scene: cloneScene(component.scene),
  };
}

export function cloneLayerDef(layer: LayerDef): LayerDef {
  return {
    id: layer.id,
    name: layer.name,
    geometry: layer.geometry.type === 'library'
      ? { type: 'library', id: layer.geometry.id }
      : {
          type: 'inline',
          nodes: cloneNodes(layer.geometry.nodes),
          edges: cloneEdges(layer.geometry.edges),
          sizeMode: layer.geometry.sizeMode,
        },
    material: layer.material.type === 'library'
      ? { type: 'library', id: layer.material.id }
      : {
          type: 'inline',
          nodes: cloneNodes(layer.material.nodes),
          edges: cloneEdges(layer.material.edges),
        },
    builtIn: layer.builtIn,
  };
}

export function cloneLibraryItem<T extends { nodes: GraphNode[]; edges: GraphEdge[] }>(item: T): T {
  return {
    ...item,
    nodes: cloneNodes(item.nodes),
    edges: cloneEdges(item.edges),
  };
}

export function cloneSlotValue(slot: GeometrySlot | MaterialSlot): GeometrySlot | MaterialSlot {
  if (slot.type === 'library') return { type: 'library', id: slot.id };
  return {
    type: 'inline',
    nodes: cloneNodes(slot.nodes),
    edges: cloneEdges(slot.edges),
    ...(slot.type === 'inline' && 'sizeMode' in slot ? { sizeMode: slot.sizeMode } : {}),
  };
}

export function cloneSceneNode(node: FrameNode | InstanceNode | LayerNode): FrameNode | InstanceNode | LayerNode {
  const transform = {
    position: vec2Clone(node.position),
    scale: vec2Clone(node.scale),
    origin: vec2Clone(node.origin),
  };
  if (node.kind === 'frame') {
    return { ...node, ...transform, childIds: [...node.childIds] };
  }
  return { ...node, ...transform, paramOverrides: { ...node.paramOverrides } };
}

export function cloneSceneNodeMap(nodes: Scene['nodes']): Scene['nodes'] {
  const out: Scene['nodes'] = {};
  for (const [id, node] of Object.entries(nodes)) {
    out[id] = cloneSceneNode(node);
  }
  return out;
}
